import axios from 'axios';
import type { AxiosInstance } from 'axios';
import type { 
  AdminLoginRequest, 
  AdminLoginResponse, 
  AdminUser,
  User,
  CreateUserRequest,
  UpdateUserRequest,
  Organization,
  PaginatedResponse,
  UserAnalytics,
  AnalyticsResponse,
  UsageTrendsResponse,
  UserDistribution,
  TopUsersResponse,
  RealtimeStatsResponse
} from './types';

import { API_BASE_URL } from '../../utils/constants';

class AdminApi {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load token from localStorage on init
    const storedToken = localStorage.getItem('adminToken');
    if (storedToken) {
      this.setToken(storedToken);
    }

    // Request interceptor to add token
    this.api.interceptors.request.use(
      (config) => {
        if (this.token && config.headers) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Clear token and redirect to login
          this.clearToken();
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Token management
  setToken(token: string) {
    this.token = token;
    localStorage.setItem('adminToken', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('adminToken');
  }

  getToken(): string | null {
    return this.token;
  }

  // Auth endpoints
  async login(credentials: AdminLoginRequest): Promise<AdminLoginResponse> {
    const response = await this.api.post<AdminLoginResponse>('/admin/auth/login', credentials);
    this.setToken(response.data.access_token);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/admin/auth/logout');
    } finally {
      this.clearToken();
    }
  }

  async getCurrentAdmin(): Promise<AdminUser> {
    const response = await this.api.get<AdminUser>('/admin/auth/me');
    return response.data;
  }

  async getPermissions(): Promise<Record<string, string[]>> {
    const response = await this.api.get<{ permissions: Record<string, string[]> }>('/admin/auth/permissions');
    return response.data.permissions;
  }

  // User management endpoints
  async getUsers(params?: {
    search?: string;
    user_type?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<User>> {
    const response = await this.api.get<{
      users: User[];
      pagination: PaginatedResponse<User>['pagination'];
    }>('/admin/users/', { params });
    
    return {
      items: response.data.users,
      pagination: response.data.pagination,
    };
  }

  async getUser(userId: string): Promise<User> {
    const response = await this.api.get<User>(`/admin/users/${userId}`);
    return response.data;
  }

  async createUser(data: CreateUserRequest): Promise<User> {
    const response = await this.api.post<User>('/admin/users/', data);
    return response.data;
  }

  async updateUser(userId: string, data: UpdateUserRequest): Promise<User> {
    const response = await this.api.put<User>(`/admin/users/${userId}`, data);
    return response.data;
  }

  async deleteUser(userId: string): Promise<void> {
    await this.api.delete(`/admin/users/${userId}`);
  }

  // Organization management
  async getOrganizations(params?: {
    search?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Organization>> {
    const response = await this.api.get<{
      organizations: Organization[];
      pagination: PaginatedResponse<Organization>['pagination'];
    }>('/admin/users/organizations/', { params });
    
    return {
      items: response.data.organizations,
      pagination: response.data.pagination,
    };
  }

  // Analytics
  async getUserAnalytics(): Promise<UserAnalytics> {
    const response = await this.api.get<UserAnalytics>('/admin/users/analytics/overview');
    return response.data;
  }

  // New Analytics endpoints
  async getAnalyticsOverview(period: string = '30d'): Promise<AnalyticsResponse> {
    const response = await this.api.get<AnalyticsResponse>('/admin/analytics/overview', {
      params: { period }
    });
    return response.data;
  }

  async getUsageTrends(period: string = '30d', granularity: string = 'daily'): Promise<UsageTrendsResponse> {
    const response = await this.api.get<UsageTrendsResponse>('/admin/analytics/usage-trends', {
      params: { period, granularity }
    });
    return response.data;
  }

  async getUserDistribution(): Promise<UserDistribution> {
    const response = await this.api.get<UserDistribution>('/admin/analytics/user-distribution');
    return response.data;
  }

  async getTopUsers(limit: number = 10, metric: string = 'messages'): Promise<TopUsersResponse> {
    const response = await this.api.get<TopUsersResponse>('/admin/analytics/top-users', {
      params: { limit, metric }
    });
    return response.data;
  }

  async getRealtimeStats(): Promise<RealtimeStatsResponse> {
    const response = await this.api.get<RealtimeStatsResponse>('/admin/analytics/realtime-stats');
    return response.data;
  }

  async trackMessage(userId: string, organizationId: string, messageCount: number = 1): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/admin/analytics/track-message', {
      user_id: userId,
      organization_id: organizationId,
      message_count: messageCount
    });
    return response.data;
  }

  // Generic get method for accessing new endpoints
  async get(url: string, params?: any): Promise<any> {
    const response = await this.api.get(url, { params });
    return response;
  }

  // User usage details endpoint
  async getUserUsageDetails(userId: string, period: string = '30d'): Promise<any> {
    const response = await this.api.get(`/admin/analytics/user/${userId}/usage`, {
      params: { period }
    });
    return response.data;
  }
}

// Export singleton instance
export const adminApi = new AdminApi();